import React, { createContext, useContext, useMemo, useCallback, useState } from "react";

const SnackbarContext = createContext(null);
export const useSnackbar = () => useContext(SnackbarContext);

let _id = 0;
const nextId = () => String(++_id);

const normalize = (payload) => {
  if (typeof payload === "string") {
    return {
      title: "",
      message: payload,
      variant: "info",
      actions: [],
      persist: false,
      duration: 6000,
      backdrop: "pulse",
      compareSkins: false,
    };
  }

  const p = payload || {};
  return {
    title: typeof p.title === "string" ? p.title : "",
    message: typeof p.message === "string" ? p.message : "",
    variant: typeof p.variant === "string" ? p.variant : "info",
    actions: Array.isArray(p.actions) ? p.actions : [],
    persist: !!p.persist,
    duration: Number.isFinite(p.duration) ? p.duration : 6000,
    backdrop: typeof p.backdrop === "string" ? p.backdrop : "pulse",
    compareSkins: !!p.compareSkins,
  };
};

export const SnackbarProvider = ({ children }) => {
  const [queue, setQueue] = useState([]);
  const [activeId, setActiveId] = useState(null);

  const active = useMemo(
    () => queue.find((t) => t.id === activeId) || null,
    [queue, activeId]
  );

const snackbar = useMemo(() => {
  return {
    open: !!active,
    id: active?.id || null,
    title: active?.title || "",
    message: active?.message || "",
    variant: active?.variant || "info",
    actions: active?.actions || [],
    persist: !!active?.persist,
    duration: active?.duration ?? 6000,
    backdrop: active ? (active.backdrop || "pulse") : null,
    compareSkins: !!active?.compareSkins,
  };
}, [active]);

  const openSnackbar = useCallback((payload) => {
    const t = normalize(payload);
    const id = nextId();
    const toast = { id, ...t };

    setQueue((prev) => [...prev, toast]);
    setActiveId((prev) => prev || id);

    return id;
  }, []);

  const closeSnackbar = useCallback((id) => {
    setQueue((prev) => {
      const targetId = id || activeId;
      const next = prev.filter((t) => t.id !== targetId);
      setActiveId((prevActiveId) =>
        prevActiveId === targetId ? (next[0]?.id ?? null) : prevActiveId
      );
      return next;
    });
  }, [activeId]);

  const value = useMemo(() => {
    return { snackbar, openSnackbar, closeSnackbar };
  }, [snackbar, openSnackbar, closeSnackbar]);

  return (
    <SnackbarContext.Provider value={value}>
      {children}
    </SnackbarContext.Provider>
  );
};